import {
  calendarResponseSchema,
  salesCycleListResponseSchema,
  type CalendarQuery,
  type CalendarResponse,
  type SalesCycle,
} from '@complan/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';
import { toCalendarSearch } from '@/shared/url/calendar-search-params';

/**
 * The two server reads the Calendar_View grid depends on (task 22.2).
 *
 * The split is the load-bearing part of "switching orientation does not
 * refetch" (R10.2):
 *
 * - **The bars/count/legend read** ({@link useCalendarBars}) is keyed by the
 *   *filter* signature with orientation folded out (`queryKeys.calendar`). The
 *   server payload — `bars`, `count`, `legend` — is identical for the month and
 *   calendar-week orientations, and the Sales_Cycle orientation re-uses the same
 *   bars, so all three orientations resolve to one cache entry and a switch only
 *   re-derives geometry on the client.
 * - **The Sales_Cycle definitions read** ({@link useSalesCycles}) is keyed by the
 *   *year* alone. Its result is what the Sales_Cycle-orientation track and the
 *   Sales Cycle band row of UI spec §4.1 are built from; it too is orientation-
 *   independent, so entering and leaving Sales_Cycle orientation never refetches
 *   it either.
 *
 * Both go through the shared `apiFetch` wrapper, so a non-2xx surfaces as a typed
 * `ApiRequestError` the route error boundary maps, and both parse the wire body
 * through the contracts schema so a drifted shape fails loudly at the boundary
 * rather than silently mis-rendering.
 */

/** Fetches the bars/count/legend for a filter (orientation is irrelevant to the payload). */
export async function fetchCalendarBars(query: CalendarQuery): Promise<CalendarResponse> {
  // Reuse the URL codec so the filter grammar is serialised exactly once, in one
  // place. `toCalendarSearch` deliberately omits `planningYear` (it lives in the
  // route path for the browser URL), so the request adds it back explicitly.
  // Orientation is pinned to `month`: the payload does not depend on it, and
  // pinning keeps the request URL stable across orientation switches, matching
  // the orientation-invariant cache key.
  const params = toCalendarSearch({
    query: { ...query, orientation: 'month' },
    sort: DEFAULT_SORT,
  });
  params.set('planningYear', String(query.planningYear));
  const body = await apiFetch<unknown>(`/api/calendar?${params.toString()}`);
  return calendarResponseSchema.parse(body);
}

/** Fetches the active Sales_Cycle definitions of one planning year, in date order. */
export async function fetchSalesCycles(planningYear: number): Promise<readonly SalesCycle[]> {
  const params = new URLSearchParams({ planningYear: String(planningYear) });
  const body = await apiFetch<unknown>(`/api/sales-cycles?${params.toString()}`);
  return salesCycleListResponseSchema.parse(body).items;
}

/** React Query hook for the bars/count/legend of the current filter. */
export function useCalendarBars(query: CalendarQuery): UseQueryResult<CalendarResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.calendar(query),
    queryFn: () => fetchCalendarBars(query),
  });
}

/** React Query hook for the Sales_Cycle definitions of a planning year. */
export function useSalesCycles(planningYear: number): UseQueryResult<readonly SalesCycle[], unknown> {
  return useQuery({
    queryKey: queryKeys.salesCycles(planningYear),
    queryFn: () => fetchSalesCycles(planningYear),
    // Cycle definitions change rarely relative to a planning session, and the
    // grid re-reads them on every orientation switch; a longer stale window keeps
    // the switch instant without risking a stale render inside a session.
    staleTime: 5 * 60 * 1000,
  });
}

const DEFAULT_SORT = { field: 'start_date', direction: 'asc' } as const;
